/**
 * schema.org JSON-LD for the DealHunt-backed routes.
 *
 * WordPress pages get a full Yoast `@graph`; these routes shipped `jsonLd: []`, so they
 * were ineligible for every rich result — no breadcrumb trail in the SERP, no review
 * stars, no product panel.
 *
 * House convention, mirrored from the Yoast output (see the `json_ld` fixture in
 * `fixtures/sources/post-page.source.ts`): ONE object per page, `{'@context', '@graph': []}`,
 * with `@id` fragments `{origin}/#website`, `{pageUrl}#webpage`, `{pageUrl}#breadcrumb`,
 * cross-referenced by bare `{'@id': …}`.
 *
 * ## Compliance rules encoded here — read before extending
 *
 * - **No `aggregateRating`, ever.** Google requires it to aggregate genuine multiple
 *   reviews of THIS product. The only multi-value rating data on a PDP is the peer-class
 *   distribution (how other products scored), and its counts are peer counts. Publishing
 *   those as `ratingCount` would claim hundreds of reviews that do not exist. A single
 *   `Review` with `reviewRating` is the correct — and still star-eligible — markup.
 * - **Every property must match visible content.** Nothing here is emitted from a value
 *   the page does not render.
 * - **`offers` only on the live-price branch.** When `priceFallback` is set the page
 *   renders "Manufacturer's suggested retail price at launch", so an Offer would
 *   contradict the page's own text.
 * - **No `availability`** — the DealHunt contract has no such field, and guessing
 *   `InStock` is a fabrication.
 * - **`offers.url` is the PDP canonical, not the affiliate redirect.** `amazonUrl()`
 *   returns a monetised short.io link on a `rel="nofollow sponsored"` anchor; putting a
 *   monetised redirect inside structured data is a smell Google's guidelines discourage.
 * - **No `FAQPage`** — Google restricted those rich results to government and health
 *   sites; it earns nothing and adds risk.
 * - **No `Product` node without `review` or `offers`.** Google's Product-snippets
 *   validator rejects any `Product` carrying none of `review` / `offers` /
 *   `aggregateRating` ("Either offers, review, or aggregateRating should be specified" —
 *   71 PDPs in Search Console, 2026-10-22). A no-review, no-live-price PDP has nothing
 *   legal to add (the three rules above), and a Product with only identity + specs
 *   qualifies for no rich result, so the node is dropped rather than shipped invalid.
 *   The spec table is SSR'd in the HTML regardless (SEO audit C1).
 */
import { DEALHUNT_ROOT_SEGMENT } from './constants'
import { dealHuntCategoryHref } from './url'
import type { DealHuntBreadcrumb } from './types'

/** Category-ish breadcrumb node types — the ones that have a browsable page. */
const CATEGORY_NODE_TYPES = new Set(['root', 'category', 'subcategory'])

const SITE_NAME = 'SoundGuys'
const SITE_DESCRIPTION = 'We Live Audio!'
const LANG = 'en-US'

type Node = Record<string, unknown>

/** Drop keys whose value is null/undefined/empty so the graph never ships empty claims. */
function compact(node: Node): Node {
  return Object.fromEntries(
    Object.entries(node).filter(([, v]) => {
      if (v === null || v === undefined || v === '') {
        return false
      }
      return !(Array.isArray(v) && v.length === 0)
    })
  )
}

/** The site node every page's WebPage hangs off, matching Yoast's `#website` @id. */
function webSiteNode(appUrl: string): Node {
  return {
    '@type': 'WebSite',
    '@id': `${appUrl}/#website`,
    url: `${appUrl}/`,
    name: SITE_NAME,
    description: SITE_DESCRIPTION,
    inLanguage: LANG,
  }
}

/**
 * BreadcrumbList from DealHunt's trail.
 *
 * Mirrors `components/product/Breadcrumbs.tsx` exactly — the root taxonomy segment is
 * filtered out (the component hides it) and only category-typed, non-final crumbs get an
 * `item` URL. Pass the TEMPLATE's branded crumb array on a PDP, not `data.breadcrumbs`:
 * the template brands the final leaf ("Bose QuietComfort 35"), and markup that says
 * "QuietComfort 35" while the page shows the branded name is a content mismatch.
 */
export function breadcrumbListNode(
  crumbs: readonly DealHuntBreadcrumb[],
  pageUrl: string,
  appUrl: string,
  trailingSlash: boolean
): Node | null {
  const visible = crumbs.filter(c => c.fullSlug !== DEALHUNT_ROOT_SEGMENT)
  if (!visible.length) {
    return null
  }
  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: visible.map((crumb, i) => {
      const isLast = i === visible.length - 1
      const linkable = CATEGORY_NODE_TYPES.has(crumb.nodeType) && !isLast
      return compact({
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
        item: linkable ? `${appUrl}${dealHuntCategoryHref(crumb.fullSlug, trailingSlash)}` : null,
      })
    }),
  }
}

export interface ProductGraphInput {
  pageUrl: string
  appUrl: string
  trailingSlash: boolean
  /** Page title, reused as the WebPage name. */
  title: string
  description: string | null
  /** The H1 expression — `productDisplayName(line)` (url.ts). */
  productName: string
  brandName: string | null
  images: Array<string | null | undefined>
  /** `node.modelNumber`. */
  mpn: string | null
  /** Category trail joined for `Product.category`. */
  category: string | null
  /** Featured spec chips, as rendered in Key Specs. */
  specs: Array<{ name: string; value: string; unit?: string | null }>
  /** Branded breadcrumb trail — `brandedBreadcrumbs(line)` (url.ts), as the page renders it. */
  breadcrumbs: readonly DealHuntBreadcrumb[]
  review: {
    score: number | null
    publishedAt: string | null
    summary: string | null
    reviewers: Array<{ name: string; profileUrl: string | null }>
    pros: string[]
    cons: string[]
  } | null
  /** Live price in cents — null when there is none (then no Offer is emitted). */
  priceCents: number | null
  currency: string | null
  /** True when the page is showing MSRP rather than a live price: suppresses `offers`. */
  isMsrpFallback: boolean
  /** Amazon ASIN of the primary variant — Google's recommended Product identifier slot
   *  when no GTIN is known (SEO audit H9). Omitted when absent, never invented. */
  sku?: string | null
  /** ISO date through which the live price is considered valid. DealHunt computes it from
   *  its Keepa refresh cadence (contract field — optional until shipped); SG never
   *  derives it locally because only DealHunt knows the per-product refresh period. */
  priceValidUntil?: string | null
}

/**
 * Product + Review (+ Offer) graph for a PDP.
 *
 * The `Product` node is present only when it carries a `review` or an `offers`; see the
 * last compliance rule in the file header.
 */
export function productGraph(input: ProductGraphInput): Node {
  const {
    pageUrl,
    appUrl,
    trailingSlash,
    title,
    description,
    productName,
    brandName,
    images,
    mpn,
    category,
    specs,
    breadcrumbs,
    review,
    priceCents,
    currency,
    isMsrpFallback,
    sku = null,
    priceValidUntil = null,
  } = input

  const breadcrumb = breadcrumbListNode(breadcrumbs, pageUrl, appUrl, trailingSlash)

  // A Review is emitted only for a real, scored SoundGuys review. `summary`, `pros` and
  // `cons` are already SoundGuys-only and placeholder-free (see lib/dealhunt/review.ts);
  // anything absent is simply omitted by `compact` rather than substituted.
  const reviewNode =
    review && review.score !== null
      ? compact({
          '@type': 'Review',
          '@id': `${pageUrl}#review`,
          // The score is normalised to /10 in review.ts. `worstRating` must be explicit:
          // Google defaults it to 1, which would silently misrepresent an 8.5.
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.score,
            bestRating: 10,
            worstRating: 0,
          },
          author: review.reviewers.length
            ? review.reviewers.map(r =>
                compact({ '@type': 'Person', name: r.name, url: r.profileUrl })
              )
            : // Never fabricate a Person — attribute to the publication instead.
              { '@type': 'Organization', name: SITE_NAME },
          publisher: { '@type': 'Organization', name: SITE_NAME },
          datePublished: review.publishedAt,
          reviewBody: review.summary,
          positiveNotes: review.pros.length
            ? {
                '@type': 'ItemList',
                itemListElement: review.pros.map((p, i) => ({
                  '@type': 'ListItem',
                  position: i + 1,
                  name: p,
                })),
              }
            : null,
          negativeNotes: review.cons.length
            ? {
                '@type': 'ItemList',
                itemListElement: review.cons.map((c, i) => ({
                  '@type': 'ListItem',
                  position: i + 1,
                  name: c,
                })),
              }
            : null,
        })
      : null

  const offers =
    !isMsrpFallback && priceCents !== null
      ? compact({
          '@type': 'Offer',
          '@id': `${pageUrl}#offer`,
          price: (priceCents / 100).toFixed(2),
          priceCurrency: currency,
          priceValidUntil,
          // The PDP canonical, deliberately not the monetised affiliate redirect.
          url: pageUrl,
        })
      : null

  // Neither a scored review nor a live price: Google rejects the bare Product and it
  // earns nothing, so the page ships WebSite + WebPage + BreadcrumbList only.
  const productEligible = reviewNode !== null || offers !== null

  const productNode = compact({
    '@type': 'Product',
    '@id': `${pageUrl}#product`,
    name: productName,
    brand: brandName ? { '@type': 'Brand', name: brandName } : null,
    image: images.filter((u): u is string => Boolean(u)),
    description,
    sku,
    mpn,
    category,
    additionalProperty: specs.map(s =>
      compact({
        '@type': 'PropertyValue',
        name: s.name,
        value: s.value,
        unitText: s.unit ?? null,
      })
    ),
    review: reviewNode ? [reviewNode] : null,
    offers,
  })

  return {
    '@context': 'https://schema.org',
    '@graph': [
      webSiteNode(appUrl),
      compact({
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: title,
        description,
        isPartOf: { '@id': `${appUrl}/#website` },
        breadcrumb: breadcrumb ? { '@id': `${pageUrl}#breadcrumb` } : null,
        inLanguage: LANG,
      }),
      ...(breadcrumb ? [breadcrumb] : []),
      ...(productEligible ? [productNode] : []),
    ],
  }
}

export interface CollectionGraphInput {
  pageUrl: string
  appUrl: string
  trailingSlash?: boolean
  title: string
  description: string | null
  /** Listed items, already resolved to ABSOLUTE urls. */
  items: Array<{ name: string; url: string }>
  /** Optional DealHunt breadcrumb trail (catalogue category pages). */
  breadcrumbs?: readonly DealHuntBreadcrumb[]
  /**
   * Optional plain trail for pages whose crumbs are not DealHunt nodes — the editorial
   * coverage pages (Home › Reviews › Headphones › Over-ear headphones). `url` is
   * ABSOLUTE; null on the final crumb, which names the page itself. Exactly the trail
   * the page renders, or markup and page disagree.
   */
  trail?: ReadonlyArray<{ name: string; url: string | null }>
  /**
   * Optional second ItemList — the "Most picked right now" strip on best hubs.
   * Summary-page style on purpose: plain ListItems whose `url` points at each
   * product's own detail page (its review), never inline Product nodes — rating
   * and offer markup belongs on the review page that carries the actual review
   * (SEO audit round 3: duplicated Review+Rating across URLs is a liability).
   * Same absolute-URL contract as `items`; `image` rides along when known.
   */
  mostPicked?: Array<{ name: string; url: string; image?: string }>
}

/**
 * CollectionPage + ItemList for a category or coverage listing.
 *
 * Callers must pass `items: []` when a filter or a non-default sort is active: the
 * canonical for those views points at the UNFILTERED page, so listing the filtered set
 * would describe a different page than the canonical claims.
 */
export function collectionGraph(input: CollectionGraphInput): Node {
  const {
    pageUrl,
    appUrl,
    trailingSlash = false,
    title,
    description,
    items,
    breadcrumbs,
    trail,
    mostPicked,
  } = input
  let breadcrumb: Node | null = null
  if (breadcrumbs) {
    breadcrumb = breadcrumbListNode(breadcrumbs, pageUrl, appUrl, trailingSlash)
  } else if (trail && trail.length > 0) {
    breadcrumb = {
      '@type': 'BreadcrumbList',
      '@id': `${pageUrl}#breadcrumb`,
      itemListElement: trail.map((crumb, i) =>
        compact({ '@type': 'ListItem', position: i + 1, name: crumb.name, item: crumb.url })
      ),
    }
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      webSiteNode(appUrl),
      compact({
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: title,
        description,
        isPartOf: { '@id': `${appUrl}/#website` },
        breadcrumb: breadcrumb ? { '@id': `${pageUrl}#breadcrumb` } : null,
        inLanguage: LANG,
      }),
      ...(breadcrumb ? [breadcrumb] : []),
      ...(items.length
        ? [
            {
              '@type': 'ItemList',
              '@id': `${pageUrl}#list`,
              numberOfItems: items.length,
              itemListElement: items.map((it, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: it.name,
                url: it.url,
              })),
            },
          ]
        : []),
      ...(mostPicked?.length
        ? [
            {
              '@type': 'ItemList',
              '@id': `${pageUrl}#most-picked`,
              name: 'Most picked right now',
              numberOfItems: mostPicked.length,
              itemListElement: mostPicked.map((it, i) =>
                compact({
                  '@type': 'ListItem',
                  position: i + 1,
                  name: it.name,
                  url: it.url,
                  image: it.image ?? null,
                })
              ),
            },
          ]
        : []),
    ],
  }
}
